package com.elan.edubridge.shared.domain.repository

import com.elan.edubridge.shared.domain.model.AcademyApplication
import com.elan.edubridge.shared.domain.model.AcademyData
import com.elan.edubridge.shared.domain.model.Assignment
import com.elan.edubridge.shared.domain.model.AssignmentSubmission
import com.elan.edubridge.shared.domain.model.AttendanceEvent
import com.elan.edubridge.shared.domain.model.AttendanceSummary
import com.elan.edubridge.shared.domain.model.AttendanceTimelineEntry
import com.elan.edubridge.shared.domain.model.CalendarEvent
import com.elan.edubridge.shared.domain.model.ClassData
import com.elan.edubridge.shared.domain.model.ContentReport
import com.elan.edubridge.shared.domain.model.DailyRosterEntry
import com.elan.edubridge.shared.domain.model.GuardianLink
import com.elan.edubridge.shared.domain.model.JoinApplication
import com.elan.edubridge.shared.domain.model.JoinApplicationStatus
import com.elan.edubridge.shared.domain.model.NoticeData
import com.elan.edubridge.shared.domain.model.PostComment
import com.elan.edubridge.shared.domain.model.PostData
import com.elan.edubridge.shared.domain.model.ReadResult
import com.elan.edubridge.shared.domain.model.StudentData
import com.elan.edubridge.shared.domain.model.TodayClass
import kotlinx.coroutines.flow.Flow

/**
 * 카탈로그·출결 조회 계약. UI는 이 인터페이스만 안다.
 *
 * 시그니처와 실패 규약은 `EduConnectDataSource`와 같다 —
 * `serviceDate`가 null이면 학원 timeZone 기준 오늘이고, 복구 가능한 실패는 빈 리스트다.
 *
 * **다만 소프트 삭제 필터는 여기서 끝난다**: 공지·게시글·댓글 조회는 `status == ACTIVE`인 것만
 * 돌려준다. 툼스톤은 이 인터페이스 위로 올라오지 않는다.
 */
interface EduConnectRepository {
    suspend fun academies(academyIds: List<String>): List<AcademyData>

    suspend fun classes(academyId: String): List<ClassData>

    suspend fun students(academyId: String): List<StudentData>

    /** 삭제되지 않은 공지만. 정렬은 기존대로 데이터소스 순서를 따른다. */
    suspend fun notices(academyId: String): List<NoticeData>

    /** 삭제되지 않은 게시글만. 최신 작성순이다. */
    suspend fun posts(academyId: String): List<PostData>

    /** 글 하나. 없거나 삭제됐으면 null이다 — 툼스톤을 상세 화면에 그리지 않기 위함이다. */
    suspend fun post(academyId: String, postId: String): PostData?

    /** 삭제되지 않은 댓글만. 스레드 순서대로(오래된 것부터) 온다. */
    suspend fun postComments(academyId: String, postId: String): List<PostComment>

    /**
     * 학원 캘린더의 한 구간(양끝 포함). 삭제되지 않은 일정만, 시작 시각 오름차순이다.
     *
     * 구간은 `startAt` 기준이라 **구간보다 먼저 시작한 일정은 포함되지 않는다** —
     * 화면이 앞뒤 여유를 두고 호출해야 한다(`EduConnectDataSource.academyEvents` 참고).
     */
    suspend fun academyEvents(
        academyId: String,
        startAt: String,
        endAt: String,
    ): List<CalendarEvent>

    /** 개인 캘린더의 한 구간. [academyEvents]와 같은 규칙이며 [userId]가 곧 접근 범위다. */
    suspend fun personalEvents(
        userId: String,
        startAt: String,
        endAt: String,
    ): List<CalendarEvent>

    suspend fun studentSchedule(academyId: String, studentUserId: String): List<ClassData>

    suspend fun studentAssignments(academyId: String, studentUserId: String): List<Assignment>

    suspend fun todayClasses(academyId: String, serviceDate: String?): List<TodayClass>

    suspend fun attendanceSummary(academyId: String, serviceDate: String?): AttendanceSummary

    suspend fun dailyRoster(
        academyId: String,
        serviceDate: String?,
        classId: String?,
    ): List<DailyRosterEntry>

    /** 신청자 본인의 학원 개설 신청 이력. 뷰어가 자기 신청 상태를 확인하는 경로다. */
    suspend fun myAcademyApplications(applicantUid: String): List<AcademyApplication>

    /**
     * 스태프 신고 대기열. rules가 스태프에게만 열어 두므로 일반 멤버가 부르면 빈 목록이다 —
     * [ContentReport.reporterUid]가 들어 있어 신고자를 노출하기 때문이다.
     */
    suspend fun contentReports(academyId: String, openOnly: Boolean): List<ContentReport>

    /**
     * 스태프 가입 신청 큐(W7). rules가 스태프에게만 열어 두므로 일반 멤버가 부르면 빈
     * 목록이다. [status]로 필터를 건다(대기 큐 PENDING, 차단 관리 BLOCKED) — 최근 제출
     * 순으로 온다.
     */
    suspend fun joinApplications(
        academyId: String,
        status: JoinApplicationStatus,
    ): List<JoinApplication> = emptyList()

    /** 본인 가입 신청 미러(W7). [userId]가 곧 접근 범위다. */
    suspend fun myJoinApplications(userId: String): List<JoinApplication> = emptyList()

    // ---- 실패를 구분하는 읽기 (#30, 단계 1) ----
    //
    // 위 메서드들과 **같은 필터·같은 정렬**을 적용하되 실패를 잃지 않는다
    // ([ReadResult] KDoc). 소프트 삭제 툼스톤 필터가 이 인터페이스에서 끝난다는 규칙은
    // 그대로다 — 아래 메서드들도 ACTIVE만 올려 보낸다.
    //
    // 기본 구현은 원본 메서드에 위임한다. 데이터소스 쪽과 같은 이유이며(테스트 페이크를
    // 포함한 구현체가 한 커밋에서 다 같이 깨지지 않게 한다), 실제 동작은
    // `EduConnectRepositoryImpl`의 override가 정한다.
    //
    // 이전이 끝나면 위쪽 원본이 삭제된다(제안서 §3.2 단계 4).

    suspend fun academiesResult(academyIds: List<String>): ReadResult<AcademyData> =
        ReadResult.of(academies(academyIds))

    suspend fun classesResult(academyId: String): ReadResult<ClassData> =
        ReadResult.of(classes(academyId))

    suspend fun studentsResult(academyId: String): ReadResult<StudentData> =
        ReadResult.of(students(academyId))

    suspend fun noticesResult(academyId: String): ReadResult<NoticeData> =
        ReadResult.of(notices(academyId))

    suspend fun postsResult(academyId: String): ReadResult<PostData> =
        ReadResult.of(posts(academyId))

    /** 0개 또는 1개. "글 없음"과 "못 읽음"이 갈린다 — [post]의 null은 둘을 뭉갠다. */
    suspend fun postResult(academyId: String, postId: String): ReadResult<PostData> =
        ReadResult.of(listOfNotNull(post(academyId, postId)))

    suspend fun postCommentsResult(academyId: String, postId: String): ReadResult<PostComment> =
        ReadResult.of(postComments(academyId, postId))

    suspend fun studentScheduleResult(
        academyId: String,
        studentUserId: String,
    ): ReadResult<ClassData> = ReadResult.of(studentSchedule(academyId, studentUserId))

    suspend fun studentAssignmentsResult(
        academyId: String,
        studentUserId: String,
    ): ReadResult<Assignment> = ReadResult.of(studentAssignments(academyId, studentUserId))

    suspend fun assignmentsResult(academyId: String): ReadResult<Assignment> =
        ReadResult.of(assignments(academyId))

    suspend fun assignmentSubmissionsResult(
        academyId: String,
        assignmentId: String,
    ): ReadResult<AssignmentSubmission> =
        ReadResult.of(assignmentSubmissions(academyId, assignmentId))

    suspend fun pendingAcademyApplicationsResult(): ReadResult<AcademyApplication> =
        ReadResult.of(pendingAcademyApplications())

    suspend fun myAcademyApplicationsResult(applicantUid: String): ReadResult<AcademyApplication> =
        ReadResult.of(myAcademyApplications(applicantUid))

    suspend fun contentReportsResult(
        academyId: String,
        openOnly: Boolean,
    ): ReadResult<ContentReport> = ReadResult.of(contentReports(academyId, openOnly))

    suspend fun joinApplicationsResult(
        academyId: String,
        status: JoinApplicationStatus,
    ): ReadResult<JoinApplication> = ReadResult.of(joinApplications(academyId, status))

    suspend fun myJoinApplicationsResult(userId: String): ReadResult<JoinApplication> =
        ReadResult.of(myJoinApplications(userId))

    // 안드로이드 레인이 추가한 3종(2026-08-17).

    suspend fun todayClassesResult(
        academyId: String,
        serviceDate: String?,
    ): ReadResult<TodayClass> = ReadResult.of(todayClasses(academyId, serviceDate))

    suspend fun academyEventsResult(
        academyId: String,
        startAt: String,
        endAt: String,
    ): ReadResult<CalendarEvent> = ReadResult.of(academyEvents(academyId, startAt, endAt))

    suspend fun personalEventsResult(
        userId: String,
        startAt: String,
        endAt: String,
    ): ReadResult<CalendarEvent> = ReadResult.of(personalEvents(userId, startAt, endAt))

    /** 기간 출결 원장(양끝 포함). 통계 화면의 원본이며 스태프 전용이다(§4.5). */
    suspend fun attendanceRecordsRange(
        academyId: String,
        startDate: String,
        endDate: String,
        classId: String?,
        studentId: String?,
    ): List<DailyRosterEntry>

    /** 개인정보 미러(§4.3). [userId]가 곧 접근 범위다. */
    suspend fun myTimeline(
        userId: String,
        academyId: String?,
        subjectStudentId: String?,
        limit: Int,
    ): List<AttendanceTimelineEntry>

    suspend fun guardianLinks(guardianUserId: String): List<GuardianLink>

    /** 한 학생의 보호자 전원. 스태프 학생 상세용이며 REVOKED 링크도 포함한다. */
    suspend fun studentGuardianLinks(academyId: String, studentId: String): List<GuardianLink>

    /** 학원 단위 과제 목록. 툼스톤은 여기까지 올라오지 않는다. */
    suspend fun assignments(academyId: String): List<Assignment>

    /**
     * 한 과제의 제출 결과. 스태프 전용이며(rules), 학생·보호자가 부르면 빈 목록이다.
     * 점수 높은 순이 아니라 **이름 순**이다 — 제출 현황은 순위표가 아니라 명부다.
     */
    suspend fun assignmentSubmissions(
        academyId: String,
        assignmentId: String,
    ): List<AssignmentSubmission> = emptyList()

    /** SUPER_ADMIN 승인 큐. 플랫폼 범위라 academyId를 받지 않는다. */
    suspend fun pendingAcademyApplications(): List<AcademyApplication>

    /** ⚠️ iOS는 M3에서 콜백 어댑터가 필요하다 — `Flow`는 ObjC로 자동 브리징되지 않는다. */
    fun attendanceEventStream(academyId: String, limit: Int): Flow<List<AttendanceEvent>>
}
